/**
 * Страница админ-режима - Enterprise Design
 * QRIUM Recvisit v2.0
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useUIStore } from '../store/uiStore'
import { BottomNavigation } from '../components/BottomNavigation'
import { EventList } from '../components/EventList'
import { ManualCheckForm } from '../components/ManualCheckForm'
import { Charts } from '../components/Charts'
import { SyncStatus } from '../components/SyncStatus'
import { CameraSettingsPanel } from '../components/CameraSettingsPanel'
import { CameraDiagnostics } from '../components/CameraDiagnostics'

type NavItem = 'events' | 'manual' | 'charts' | 'sync' | 'more'
type MoreItem = 'camera' | 'diagnostics'

export function AdminPage() {
  const [activeNav, setActiveNav] = useState<NavItem>('events')
  const [moreItem, setMoreItem] = useState<MoreItem>('camera')
  const autoLogoutTimerRef = useRef<number | null>(null)
  const uiStore = useUIStore()

  // Автоматический выход через 15 минут неактивности
  const resetTimer = useCallback(() => {
    if (autoLogoutTimerRef.current) {
      clearTimeout(autoLogoutTimerRef.current)
    }
    autoLogoutTimerRef.current = window.setTimeout(() => {
      uiStore.setAdminMode(false)
    }, 15 * 60 * 1000)
  }, [uiStore])

  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach((event) => {
      window.addEventListener(event, resetTimer)
    })
    resetTimer()

    return () => {
      if (autoLogoutTimerRef.current) {
        clearTimeout(autoLogoutTimerRef.current)
      }
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer)
      })
    }
  }, [resetTimer])

  const handleLogout = () => {
    uiStore.setAdminMode(false)
  }

  const getPageTitle = () => {
    switch (activeNav) {
      case 'events': return 'События'
      case 'manual': return 'Ручная отметка'
      case 'charts': return 'Статистика'
      case 'sync': return 'Синхронизация'
      case 'more': return 'Настройки'
      default: return 'Админ-панель'
    }
  }

  return (
    <div className="min-h-screen min-h-[100dvh] max-h-screen max-h-[100dvh] bg-black flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 bg-black/90 backdrop-blur-xl sticky top-0 z-20 border-b border-[#1f1f1f]">
        <div className="flex items-center justify-between" style={{height: '64px', padding: '0 28px'}}>
          <h1 className="text-[20px] md:text-[22px] font-semibold text-white tracking-tight">
            {getPageTitle()}
          </h1>
          <button
            onClick={handleLogout}
            className="btn btn-sm btn-secondary"
          >
            Выйти
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto smooth-scroll">
        <div className="admin-content-tablet">
          {activeNav === 'events' && <EventList />}
          {activeNav === 'manual' && <ManualCheckForm />}
          {activeNav === 'charts' && <Charts />}
          {activeNav === 'sync' && <SyncStatus />}
          {activeNav === 'more' && (
            <MoreMenu 
              activeItem={moreItem} 
              onItemChange={setMoreItem} 
            />
          )}
        </div>
      </main>

      {/* Bottom Navigation */}
      <BottomNavigation 
        active={activeNav} 
        onChange={setActiveNav} 
      />
    </div>
  )
}

function MoreMenu({ activeItem, onItemChange }: { 
  activeItem: MoreItem
  onItemChange: (item: MoreItem) => void 
}) {
  return (
    <div className="space-y-section">
      {/* Menu Items */}
      <div className="card-grouped">
        <button
          onClick={() => onItemChange('camera')}
          className={`list-item w-full text-left ${activeItem === 'camera' ? 'bg-[#242424]' : ''}`}
        >
          <div className="list-item-icon bg-[#3b82f6]/15">📷</div>
          <div className="list-item-content">
            <p className="list-item-title">Настройки камеры</p>
            <p className="list-item-subtitle">Выбор камеры и качество</p>
          </div>
          <span className="text-[#6b6b6b] text-lg">›</span>
        </button>
        
        <button
          onClick={() => onItemChange('diagnostics')}
          className={`list-item w-full text-left ${activeItem === 'diagnostics' ? 'bg-[#242424]' : ''}`}
        >
          <div className="list-item-icon bg-[#22c55e]/15">🔬</div>
          <div className="list-item-content">
            <p className="list-item-title">Диагностика</p>
            <p className="list-item-subtitle">Проверка системы</p>
          </div>
          <span className="text-[#6b6b6b] text-lg">›</span>
        </button>
      </div>
      
      {activeItem === 'camera' && (
        <div className="animate-fade-in">
          <CameraSettingsPanel />
        </div>
      )}
      {activeItem === 'diagnostics' && (
        <div className="animate-fade-in">
          <CameraDiagnostics />
        </div>
      )}
    </div>
  )
}
